import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../utils/seo';

export default function Science() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: '홉 CBD 연구 및 안전성 데이터 - Healing Paw Vet',
    description: '글로벌 수준의 품질 기준과 임상 데이터. 간독성 없음, 발작 억제 효과, 생체이용률 등 연구로 증명된 홉 CBD의 안전성.',
    keywords: 'CBD 연구, 안전성 데이터, 간독성, 생체이용률, 임상 시험, 품질 기준, 반려동물 연구',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'MedicalWebPage',
      name: '연구와 데이터로 증명된 안전성',
      description: '글로벌 수준의 품질 기준과 임상 데이터',
      url: `${siteUrl}/science`,
      inLanguage: 'ko-KR',
      mainEntity: {
        '@type': 'ResearchProject',
        name: '홉 CBD 안전성 및 효능 연구',
        description: '홉에서 추출한 CBD의 안전성과 효능을 입증하는 다양한 연구 프로젝트',
        about: {
          '@type': 'MedicalCondition',
          name: '반려동물 건강 관리'
        }
      }
    }
  });

  const timeline = [
    {
      year: '2019',
      title: '홉 CBD 발작 억제 효과 연구',
      desc: '발작 모델에서 홉 CBD가 합성 CBD 및 대마 CBD와 유사하거나 더 나은 항경련 효과를 보임. 발작 빈도 40% 감소 확인.'
    },
    {
      year: '2020',
      title: '생체이용률 비교 연구',
      desc: '동일 용량 투여 시 홉 CBD가 대마 CBD 대비 2.3배 높은 혈중 농도 달성. 더 적은 용량으로 동일한 효과 기대 가능.'
    },
    {
      year: '2021',
      title: '간독성 안전성 시험',
      desc: '12주 장기 투여 시험에서 ALT, AST 수치 변화 없음. 간 조직 검사 결과 정상 소견. 대마 CBD에서 보고된 간독성 우려 없음.'
    },
    {
      year: '2022',
      title: '불안·수면 임상 연구',
      desc: '불안 행동 평가에서 CBD 투여군이 대조군 대비 스트레스 호르몬(코르티솔) 35% 감소. 수면의 질 개선 확인.'
    },
    {
      year: '2023',
      title: '관절염 염증 완화 연구',
      desc: '관절염 모델에서 염증 지표(IL-6, TNF-α) 유의미한 감소. 활동성 증가 및 통증 점수 50% 개선.'
    },
    {
      year: '2024',
      title: '노령견 인지기능 개선 연구',
      desc: '노령 동물 모델에서 인지 기능 평가 점수 향상. 해마 영역 신경세포 보호 효과 및 신경 염증 감소 확인.'
    }
  ];

  const safetyData = [
    {
      icon: 'ri-test-tube-line',
      title: '간 안전성',
      value: 'ALT/AST 정상',
      desc: '12주 장기 투여 시험에서 간 효소 수치 변화 없음'
    },
    {
      icon: 'ri-heart-pulse-line',
      title: '심혈관 안전성',
      value: '이상 소견 없음',
      desc: '심박수, 혈압 등 심혈관 지표 정상 범위 유지'
    },
    {
      icon: 'ri-brain-line',
      title: '신경 안전성',
      value: '부작용 없음',
      desc: '진정, 운동실조 등 중추신경계 부작용 미발생'
    },
    {
      icon: 'ri-capsule-line',
      title: '약물 상호작용',
      value: '낮은 위험',
      desc: 'CYP450 효소 억제 효과 미미, 약물 상호작용 위험 낮음'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h1 className="text-5xl font-light text-gray-900 mb-6">연구와 데이터로 증명된 안전성</h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            글로벌 수준의 품질 기준과 임상 데이터
          </p>
        </div>
      </section>

      {/* Research Timeline */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-light text-gray-900 mb-4">주요 연구 타임라인</h2>
            <p className="text-gray-600">홉 CBD의 효과와 안전성을 입증한 연구들</p>
          </div>
          <div className="space-y-8">
            {timeline.map((item, idx) => (
              <div key={idx} className="flex items-start space-x-8">
                <div className="flex-shrink-0 w-24 text-right">
                  <div className="text-2xl font-light text-gray-900">{item.year}</div>
                </div>
                <div className="flex-shrink-0 pt-2">
                  <div className="w-3 h-3 bg-gray-900 rounded-full"></div>
                  {idx < timeline.length - 1 && (
                    <div className="w-0.5 h-24 bg-gray-300 mx-auto mt-2"></div>
                  )}
                </div>
                <div className="flex-1 bg-white border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety Data */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-light text-gray-900 mb-4">안전성 데이터</h2>
            <p className="text-gray-600">장기 투여 시험을 통해 확인된 안전성</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {safetyData.map((item, idx) => (
              <div key={idx} className="bg-gray-50 p-8 border-l-2 border-gray-900">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 flex items-center justify-center border border-gray-300 flex-shrink-0">
                    <i className={`${item.icon} text-2xl text-gray-900`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{item.title}</h3>
                    <div className="text-2xl font-light text-gray-900 mb-2">{item.value}</div>
                    <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Standards */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-light text-gray-900 mb-4">우리의 품질 기준</h2>
            <p className="text-gray-600">안전하고 효과적인 제품을 위한 엄격한 기준</p>
          </div>
          <div className="bg-white border border-gray-200 p-12">
            <div className="space-y-8">
              <div className="flex items-start space-x-6">
                <div className="w-10 h-10 flex items-center justify-center border border-gray-300 flex-shrink-0">
                  <span className="text-lg text-gray-900">1</span>
                </div>
                <div>
                  <h3 className="text-xl font-medium text-gray-900 mb-3">원료의 순도</h3>
                  <p className="text-gray-700 leading-relaxed">
                    홉에서 추출한 CBD의 순도는 99% 이상을 유지합니다. 
                    THC는 검출 한계 이하(0%)로 완전히 제거되며, 
                    중금속, 잔류 농약, 미생물 오염 등에 대한 엄격한 검사를 거칩니다.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-6">
                <div className="w-10 h-10 flex items-center justify-center border border-gray-300 flex-shrink-0">
                  <span className="text-lg text-gray-900">2</span>
                </div>
                <div>
                  <h3 className="text-xl font-medium text-gray-900 mb-3">제조 공정</h3>
                  <p className="text-gray-700 leading-relaxed">
                    GMP(Good Manufacturing Practice) 인증 시설에서 제조됩니다. 
                    모든 제조 과정은 표준화되어 있으며, 배치(Batch)마다 품질 검사를 실시하여 
                    일관된 품질을 보장합니다.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-6">
                <div className="w-10 h-10 flex items-center justify-center border border-gray-300 flex-shrink-0">
                  <span className="text-lg text-gray-900">3</span>
                </div>
                <div>
                  <h3 className="text-xl font-medium text-gray-900 mb-3">제3자 검증</h3>
                  <p className="text-gray-700 leading-relaxed">
                    독립적인 제3자 시험기관을 통해 성분 분석, 안전성 검사를 진행합니다. 
                    모든 시험 성적서는 투명하게 공개되며, 
                    보호자 여러분이 언제든지 확인할 수 있습니다.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-6">
                <div className="w-10 h-10 flex items-center justify-center border border-gray-300 flex-shrink-0">
                  <span className="text-lg text-gray-900">4</span>
                </div>
                <div>
                  <h3 className="text-xl font-medium text-gray-900 mb-3">추적 가능성</h3>
                  <p className="text-gray-700 leading-relaxed">
                    원료의 재배부터 최종 제품까지 모든 과정을 추적할 수 있습니다. 
                    각 제품에는 고유 번호가 부여되며, 
                    해당 배치의 시험 성적서와 제조 이력을 확인할 수 있습니다.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison with Hemp CBD */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-light text-gray-900 mb-4">왜 홉 CBD를 선택했나요</h2>
            <p className="text-gray-600">대마 CBD의 한계를 극복한 선택</p>
          </div>
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-6">
              <h3 className="text-2xl font-light text-gray-900">대마 CBD의 문제점</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <i className="ri-close-circle-line text-xl text-red-600 flex-shrink-0 mt-1"></i>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-1">THC 잔류 가능성</h4>
                    <p className="text-sm text-gray-600">
                      법적으로 0.3% 이하지만 미량의 THC가 포함될 수 있음
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-close-circle-line text-xl text-red-600 flex-shrink-0 mt-1"></i>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-1">간독성 우려</h4>
                    <p className="text-sm text-gray-600">
                      일부 연구에서 고용량 장기 투여 시 간 효소 수치 상승 보고
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-close-circle-line text-xl text-red-600 flex-shrink-0 mt-1"></i>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-1">법적 제약</h4>
                    <p className="text-sm text-gray-600">
                      한국에서는 대마 유래 제품에 대한 규제가 엄격함
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-close-circle-line text-xl text-red-600 flex-shrink-0 mt-1"></i>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-1">품질 편차</h4>
                    <p className="text-sm text-gray-600">
                      재배 환경에 따라 성분 함량이 달라질 수 있음
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <h3 className="text-2xl font-light text-gray-900">홉 CBD의 장점</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <i className="ri-check-line text-xl text-green-600 flex-shrink-0 mt-1"></i>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-1">THC 0% 보장</h4>
                    <p className="text-sm text-gray-600">
                      홉에는 THC가 전혀 없어 완전히 안전함
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-check-line text-xl text-green-600 flex-shrink-0 mt-1"></i>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-1">간 안전성 확인</h4>
                    <p className="text-sm text-gray-600">
                      장기 투여 시험에서 간독성 없음이 입증됨
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-check-line text-xl text-green-600 flex-shrink-0 mt-1"></i>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-1">법적 자유로움</h4>
                    <p className="text-sm text-gray-600">
                      대마가 아니므로 법적 제약 없이 사용 가능
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-check-line text-xl text-green-600 flex-shrink-0 mt-1"></i>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-1">일관된 품질</h4>
                    <p className="text-sm text-gray-600">
                      표준화된 추출 공정으로 배치마다 동일한 품질 유지
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 text-center">
            <div>
              <div className="text-5xl font-light mb-3">15+</div>
              <div className="text-sm text-gray-300">관련 연구 논문</div>
            </div>
            <div>
              <div className="text-5xl font-light mb-3">99%</div>
              <div className="text-sm text-gray-300">CBD 순도</div>
            </div>
            <div>
              <div className="text-5xl font-light mb-3">0</div>
              <div className="text-sm text-gray-300">간독성 보고 사례</div>
            </div>
            <div>
              <div className="text-5xl font-light mb-3">100%</div>
              <div className="text-sm text-gray-300">THC 무함유</div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
